import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';

interface UnitDetails {
  value: number;
  unit: string;
}

@Component({
  selector: 'insight-action-card',
  template: `
    <div class="insight-card" [style.height]="cardHeight">
      <div
        class="card-content"
        [style.min-width]="contentMinWidth ? contentMinWidth : ''"
        [style.max-height]="contentMaxHeight"
      >
        <!-- Icon & Title-->
        <div class="card-icon-container" *ngIf="iconPath">
          <img [src]="iconPath" />
        </div>
        <div class="card-headline" [style.font-size]="headlineFontSize">{{ cardHeadline }}</div>
        <div
          class="card-sub-headline"
          *ngIf="cardSubHeadline"
          [style.font-size]="subHeadlineFontSize"
          [style.color]="subHeadlineColor"
        >
          {{ cardSubHeadline }}
        </div>

        <!-- Thumbnail image & body text -->
        <div fxLayout="row" class="card-body" *ngIf="cardThumbnailPath || cardBodyText">
          <img class="card-image" *ngIf="cardThumbnailPath" [src]="cardThumbnailPath" />
          <small *ngIf="cardBodyText" [innerHTML]="cardBodyText | convertTrustedVrmHtml"></small>
        </div>
      </div>

      <!-- Action & percentage -OR- mini-cards -->
      <!-- Action & percentage -->
      <div
        class="action-percentage-container"
        fxLayout="row"
        fxLayoutAlign="end"
        *ngIf="!hasMiniCards && (actionText || percentage)"
      >
        <button
          class="custom-button"
          style="height: 29px; color: white"
          vrm-button
          *ngIf="actionText"
          (click)="actionTriggered()"
        >
          {{ actionText | uppercase }}
        </button>
        <div class="percentage" *ngIf="percentage" [style.color]="percentageColor" fxFlex>
          {{ percentage }}
        </div>
      </div>

      <!-- Mini cards -->
      <div *ngIf="hasMiniCards">
        <div fxLayout="row" fxFlex *ngFor="let miniCard of miniFooterCards">
          <insight-mini-card
            fxFlex
            [text]="miniCard.text"
            [percentage]="miniCard.percentage"
            [percentageColor]="miniCard.percentageColor"
          ></insight-mini-card>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .insight-card {
        background-color: white;
        border-right: 1px solid #cccccc70;
        border-bottom: 1px solid #cccccc70;
      }
      .card-content {
        height: inherit;
        max-width: 450px;
        overflow-y: auto;
        padding: 20px;
      }
      .card-icon-container {
        min-height: 50px;
      }
      .card-icon-container img {
        width: 28px;
        margin-bottom: 10px;
      }
      .card-headline {
        font-weight: 800;
      }
      .card-sub-headline {
        font-weight: bold;
      }
      .card-body {
        margin-top: 10px;
      }
      .card-image {
        width: 25%;
        height: fit-content;
        margin-right: 10px;
        margin-top: 2px;
      }

      .action-percentage-container {
        margin-top: 10px;
        padding-left: 20px;
        padding-right: 20px;
      }
      .percentage {
        font-size: 30px;
        text-align: right;
      }
    `,
  ],
})
export class InsightCardComponent implements OnInit, OnChanges {
  @Input() iconPath?: string;
  @Input() cardThumbnailPath?: string;
  @Input() cardBodyText?: string;
  @Input() cardHeight: string = '230px';
  @Input() contentMinWidth?: string;
  contentMaxHeight: string = '140px';

  @Input() cardHeadline: string;
  @Input() headlineFontSize: string = '18px';

  @Input() cardSubHeadline?: string;
  @Input() subHeadlineColor: string = '#ccc';
  @Input() subHeadlineFontSize: string = '16px';

  @Input() actionText?: string;
  @Output() actionEvent: EventEmitter<any> = new EventEmitter();

  @Input() percentage?: string;
  @Input() percentageColor?: string;

  @Input() miniFooterCards: Array<{ text: string; percentage: string; percentageColor?: string }> = [];
  hasMiniCards: boolean = false;

  constructor() {}

  ngOnInit() {
    if (this.percentage && !this.percentage.includes('%')) {
      this.percentage = this.percentage + '%';
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['miniFooterCards']) {
      if (this.miniFooterCards && this.miniFooterCards.length) {
        this.hasMiniCards = true;
      }
    }

    if (changes['hasMiniCards'] || changes['actionText'] || changes['percentage']) {
      if (this.cardHeight && this.cardHeight.includes('px')) {
        const cardHasFooter = this.hasMiniCards || this.actionText || this.percentage;
        const cardHeightDetails: UnitDetails | null = this.getStyleUnitDetails(this.cardHeight);
        const contentMaxHeightDetails: UnitDetails | null = this.getStyleUnitDetails(this.contentMaxHeight);
        const footerHeightOffsetInPx: number = 50;

        if (!cardHasFooter && cardHeightDetails && contentMaxHeightDetails) {
          const newMaxHeight = contentMaxHeightDetails.value + footerHeightOffsetInPx;
          if (newMaxHeight <= cardHeightDetails.value) {
            this.contentMaxHeight = newMaxHeight + 'px';
          }
        }
      }
    }
  }

  private getStyleUnitDetails(styleWithInit: string): UnitDetails | null {
    const units = ['px', '%', 'rem', 'em', 'vh', 'vw'];
    let unitInUse: string = '';
    let unitValue: number;

    for (const unit of units) {
      if (styleWithInit.includes(unit)) {
        unitInUse = unit;
        break;
      }
    }

    if (unitInUse) {
      unitValue = Number(styleWithInit.replace(unitInUse, ''));
    }

    if (isNaN(unitValue)) {
      return null;
    }

    return { value: unitValue, unit: unitInUse };
  }

  actionTriggered() {
    this.actionEvent.emit();
  }
}
